/**
 * URL <-> navigation state.
 *
 * Paths are matched with react-router (`matchRoutes`) against route objects
 * generated from the screen configuration in LinkingConfiguration. The
 * resulting state objects, and the paths generated back from state, follow
 * @react-navigation/core 6.4 (getStateFromPath / getPathFromState /
 * getActionFromState) so URLs, query strings and deep-link fallbacks are
 * unchanged.
 */
import { matchRoutes, RouteObject } from 'react-router-dom'
import {
  Action,
  NavigationState,
  PartialRoute,
  PartialState,
  Route
} from './routers'

export type ScreenConfig =
  | string
  | {
      path?: string
      screens?: Record<string, ScreenConfig>
    }

export interface PathConfig {
  screens: Record<string, ScreenConfig>
}

type AnyState = NavigationState | PartialState

const joinPaths = (...paths: string[]): string =>
  ([] as string[])
    .concat(...paths.map(p => p.split('/')))
    .filter(Boolean)
    .join('/')

// ---------------------------------------------------------------------------
// Query strings (query-string compatible for the flat values the app uses)
// ---------------------------------------------------------------------------

const strictEncode = (value: string) =>
  encodeURIComponent(value).replace(
    /[!'()*]/g,
    c => `%${c.charCodeAt(0).toString(16).toUpperCase()}`
  )

const stringifyQuery = (params: Record<string, any>) =>
  Object.keys(params)
    .filter(key => params[key] !== undefined)
    .map(key =>
      params[key] === null
        ? strictEncode(key)
        : `${strictEncode(key)}=${strictEncode(String(params[key]))}`
    )
    .join('&')

const parseQuery = (query: string | undefined) => {
  const result: Record<string, any> = {}
  if (!query) {
    return result
  }
  new URLSearchParams(query).forEach((value, key) => {
    if (result[key] === undefined) {
      result[key] = value
    } else {
      result[key] = [].concat(result[key], value as any)
    }
  })
  return Object.keys(result)
    .sort()
    .reduce<Record<string, any>>((acc, key) => {
      acc[key] = result[key]
      return acc
    }, {})
}

// ---------------------------------------------------------------------------
// getStateFromPath
// ---------------------------------------------------------------------------

interface RouteHandle {
  routeNames: string[]
  paramNames: string[]
}

const routeObjectsCache = new WeakMap<PathConfig, RouteObject[]>()

/** Leaf route objects for react-router, one per URL-bearing screen. */
export function createRouteObjects(options: PathConfig): RouteObject[] {
  const cached = routeObjectsCache.get(options)
  if (cached) {
    return cached
  }

  const routes: RouteObject[] = []

  const walk = (
    screens: Record<string, ScreenConfig>,
    parentPattern: string,
    parentNames: string[]
  ) => {
    Object.entries(screens).forEach(([name, config]) => {
      const routeNames = [...parentNames, name]
      const add = (ownPath: string, pattern: string) => {
        const handle: RouteHandle = {
          routeNames,
          paramNames: ownPath
            .split('/')
            .filter(p => p.startsWith(':'))
            .map(p => p.replace(/^:/, '').replace(/\?$/, ''))
        }
        routes.push({ path: pattern || '/', caseSensitive: true, handle })
      }

      if (typeof config === 'string') {
        add(config, joinPaths(parentPattern, config))
      } else {
        let pattern = parentPattern
        if (typeof config.path === 'string') {
          pattern = joinPaths(parentPattern, config.path)
          add(config.path, pattern)
        }
        if (config.screens) {
          walk(config.screens, pattern, routeNames)
        }
      }
    })
  }

  walk(options.screens, '', [])
  routeObjectsCache.set(options, routes)
  return routes
}

export function getStateFromPath(
  path: string,
  options: PathConfig
): PartialState | undefined {
  const pathname = path.replace(/\/+/g, '/').replace(/\?.*$/, '')

  // A bare "/" only resolves to a screen configured with an empty path.
  if (pathname.replace(/^\//, '') === '') {
    return undefined
  }

  const matches = matchRoutes(createRouteObjects(options), pathname)
  if (!matches || !matches.length) {
    return undefined
  }

  const { route, params } = matches[matches.length - 1]
  const handle = route.handle as RouteHandle

  const parsed: PartialRoute[] = handle.routeNames.map((name, i) => {
    if (i !== handle.routeNames.length - 1) {
      return { name }
    }
    const ownParams = handle.paramNames.reduce<Record<string, string>>(
      (acc, key) => {
        if (params[key]) {
          acc[key] = params[key] as string
        }
        return acc
      },
      {}
    )
    return Object.keys(ownParams).length
      ? { name, params: ownParams }
      : { name }
  })

  return createNestedStateObject(path, parsed)
}

const createStateObject = (
  route: PartialRoute,
  isEmpty: boolean
): PartialState =>
  isEmpty
    ? { routes: [route] }
    : { routes: [{ ...route, state: { routes: [] } }] }

const createNestedStateObject = (
  path: string,
  routes: PartialRoute[]
): PartialState => {
  let route = routes.shift() as PartialRoute
  const state = createStateObject(route, routes.length === 0)

  if (routes.length > 0) {
    let nestedState = state

    while ((route = routes.shift() as PartialRoute)) {
      const nestedStateIndex =
        nestedState.index || nestedState.routes.length - 1

      nestedState.routes[nestedStateIndex].state = createStateObject(
        route,
        routes.length === 0
      )

      if (routes.length > 0) {
        nestedState = nestedState.routes[nestedStateIndex].state as PartialState
      }
    }
  }

  const focused = findFocusedRoute(state) as PartialRoute
  focused.path = path

  const query = parseQuery(path.split('?')[1])
  if (Object.keys(query).length) {
    focused.params = { ...focused.params, ...query }
  }

  return state
}

export function findFocusedRoute(
  state: AnyState
): Route | PartialRoute | undefined {
  let current: AnyState | undefined = state

  while (current?.routes[current.index ?? 0].state != null) {
    current = current.routes[current.index ?? 0].state
  }

  return current?.routes[current?.index ?? 0]
}

// ---------------------------------------------------------------------------
// getPathFromState
// ---------------------------------------------------------------------------

interface ConfigItem {
  pattern?: string
  screens?: Record<string, ConfigItem>
}

const createConfigItem = (
  config: ScreenConfig,
  parentPattern?: string
): ConfigItem => {
  if (typeof config === 'string') {
    const pattern = parentPattern ? joinPaths(parentPattern, config) : config
    return { pattern }
  }

  const pattern = joinPaths(parentPattern || '', config.path || '')
  const screens = config.screens
    ? createNormalizedConfigs(config.screens, pattern)
    : undefined

  return {
    pattern: pattern?.split('/').filter(Boolean).join('/'),
    screens
  }
}

const createNormalizedConfigs = (
  options: Record<string, ScreenConfig>,
  pattern?: string
) =>
  Object.fromEntries(
    Object.entries(options).map(([name, c]) => [
      name,
      createConfigItem(c, pattern)
    ])
  )

const getParamName = (pattern: string) =>
  pattern.replace(/^:/, '').replace(/\?$/, '')

const getActiveRoute = (state: AnyState): Route | PartialRoute => {
  const route =
    typeof state.index === 'number'
      ? state.routes[state.index]
      : state.routes[state.routes.length - 1]

  if (route.state) {
    return getActiveRoute(route.state)
  }

  return route
}

export function getPathFromState(state: AnyState, options: PathConfig): string {
  const configs: Record<string, ConfigItem> = createNormalizedConfigs(
    options.screens
  )

  let path = '/'
  let current: AnyState | undefined = state

  const allParams: Record<string, any> = {}

  while (current) {
    let index = typeof current.index === 'number' ? current.index : 0
    let route = current.routes[index] as Route | PartialRoute

    let pattern: string | undefined
    let focusedParams: Record<string, any> | undefined
    const focusedRoute = getActiveRoute(state)
    let currentOptions = configs
    const nestedRouteNames: string[] = []
    let hasNext = true

    while (route.name in currentOptions && hasNext) {
      pattern = currentOptions[route.name].pattern

      nestedRouteNames.push(route.name)

      if (route.params) {
        const currentParams = Object.fromEntries(
          Object.entries(route.params).map(([key, value]) => [
            key,
            String(value)
          ])
        )

        if (pattern) {
          Object.assign(allParams, currentParams)
        }

        if (focusedRoute === route) {
          focusedParams = { ...currentParams }

          pattern
            ?.split('/')
            .filter(p => p.startsWith(':'))
            .forEach(p => {
              if (focusedParams) {
                delete focusedParams[getParamName(p)]
              }
            })
        }
      }

      if (!currentOptions[route.name].screens || route.state === undefined) {
        hasNext = false
      } else {
        index =
          typeof route.state.index === 'number'
            ? route.state.index
            : route.state.routes.length - 1

        const nextRoute = route.state.routes[index]
        const nestedConfig = currentOptions[route.name].screens

        if (nestedConfig && nextRoute.name in nestedConfig) {
          route = nextRoute
          currentOptions = nestedConfig
        } else {
          hasNext = false
        }
      }
    }

    if (pattern === undefined) {
      pattern = nestedRouteNames.join('/')
    }

    if (currentOptions[route.name] !== undefined) {
      path += pattern
        .split('/')
        .map(p => {
          const name = getParamName(p)

          if (p === '*') {
            return route.name
          }

          if (p.startsWith(':')) {
            const value = allParams[name]

            if (value === undefined && p.endsWith('?')) {
              return ''
            }

            return encodeURIComponent(value)
          }

          return encodeURIComponent(p)
        })
        .join('/')
    } else {
      path += encodeURIComponent(route.name)
    }

    if (!focusedParams) {
      focusedParams = focusedRoute.params
    }

    if (route.state) {
      path += '/'
    } else if (focusedParams) {
      for (const param in focusedParams) {
        if (focusedParams[param] === 'undefined') {
          delete focusedParams[param]
        }
      }

      const query = stringifyQuery(focusedParams)

      if (query) {
        path += `?${query}`
      }
    }

    current = route.state
  }

  path = path.replace(/\/+/g, '/')
  path = path.length > 1 ? path.replace(/\/$/, '') : path

  return path
}

// ---------------------------------------------------------------------------
// getActionFromState
// ---------------------------------------------------------------------------

export function getActionFromState(state: PartialState): Action | undefined {
  const routes =
    state.index != null ? state.routes.slice(0, state.index + 1) : state.routes

  if (routes.length === 0) {
    return undefined
  }

  if (!(routes.length === 1 && routes[0].key === undefined)) {
    return { type: 'RESET', payload: state }
  }

  const route = state.routes[state.index ?? state.routes.length - 1]

  let current: AnyState | undefined = route?.state
  let params: any = { ...route.params }

  const payload = route
    ? { name: route.name, path: route.path, params }
    : undefined

  while (current) {
    if (current.routes.length === 0) {
      return undefined
    }

    const nestedRoutes =
      current.index != null
        ? current.routes.slice(0, current.index + 1)
        : current.routes
    const nested = nestedRoutes[nestedRoutes.length - 1]

    Object.assign(params, {
      initial: undefined,
      screen: undefined,
      params: undefined,
      state: undefined
    })

    if (nestedRoutes.length === 1 && nestedRoutes[0].key === undefined) {
      params.initial = true
      params.screen = nested.name
    } else {
      params.state = current
      break
    }

    if (nested.state) {
      params.params = { ...nested.params }
      params = params.params
    } else {
      params.path = nested.path
      params.params = nested.params
    }

    current = nested.state
  }

  if (!payload) {
    return undefined
  }

  return { type: 'NAVIGATE', payload }
}
